import { Component, OnInit, NgModule, Input, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Farm, FarmLocations, Product, Review } from '../../../models/types';
import { MarketPageService } from '../../services/market-page.service';
import { FormsModule } from '@angular/forms';
import { LoginService } from '../../services/login.service';
import Swal from 'sweetalert2';
import { MapComponent } from "../../components/map/map.component";
import { MapService } from '../../services/map.service';
import { FullCalendarComponent } from "../../components/full-calendar/full-calendar.component";
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faMagnifyingGlass, faCow, faStar, faLocationDot, faLocationCrosshairs, faTractor} from '@fortawesome/free-solid-svg-icons';
import { StarRatingModule } from 'angular-star-rating';
import { ProductListComponent } from "../../components/product-list/product-list.component";
import { RouterOutlet, RouterModule } from '@angular/router';
import { ProfilePageService } from '../../services/profile-page.service';

@Component({
  selector: 'app-farm-list',
  standalone: true,
  imports: [CommonModule, FormsModule, MapComponent, FullCalendarComponent, FontAwesomeModule, StarRatingModule, ProductListComponent, RouterModule, RouterOutlet],
  templateUrl: './farm-list.component.html',
  styleUrl: './farm-list.component.scss'
})
export class FarmListComponent {
  @Input() allFarms: Farm[] = [];
  farmCount: number = 0;
  farmLocations: FarmLocations[] = [];
  @Input() isOnMarketPage: boolean = true;
  @Input() isOnAdminPage: boolean = false;
  currentFarm: Farm | undefined = undefined;

  faMagnifyingGlass = faMagnifyingGlass;
  faCow = faCow;
  faStar = faStar;
  faLocationDot = faLocationDot;
  faLocationCrosshairs = faLocationCrosshairs;
  faTractor = faTractor;

  farmDefaultImageUrl = '../../../assets/images/generic/farm2.png';
  constructor(
    private marketPageService: MarketPageService,
    private loginService: LoginService,
    private mapService: MapService,
    private profilePageService: ProfilePageService) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['allFarms'] && !changes['allFarms'].firstChange) {
      console.log('Farm input changed:', this.allFarms);
      // Perform actions based on the updated products input
    }
  }

  subscribeToFarm(farm: Farm) {
    if (this.loginService.IsUserLoggedIn()) {
      const subscriberID = this.loginService.GetLoggedInUserInfo()!.userID;
  
      Swal.fire({
        title: 'Obvestilo o naročnini',
        text: 'Z naročnino boste prejemali novice o dogodkih in novih izdelkih s te kmetije.',
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: 'Naroči se',
        cancelButtonText: 'Prekliči'
      }).then((result) => {
        if (result.isConfirmed) {
          this.marketPageService.subscribeToFarm(farm, subscriberID).subscribe(
            (response) => {
              console.log('Subscribe Response:', response);
              if (response.status === 201) {
                // Subscription added successfully
                console.log('Subscribed to farm:', farm);
                Swal.fire({
                  title: 'Naročeni ste na kmetijo',
                  text: 'Na email boste prejeli obvestila o novih izdelkih, ponudbah in dogodkih.',
                  icon: 'success',
                  confirmButtonText: 'OK'
                });
              } else {
                // Handle other status codes if needed
                console.error('Unexpected status:', response.status);
                Swal.fire({
                  title: 'Napaka',
                  text: 'Prišlo je do napake pri naročilu na kmetijo. Poskusite znova kasneje.',
                  icon: 'error',
                  confirmButtonText: 'OK'
                });
              }
            },
            (error) => {
              console.error('Error subscribing to farm:', error);
              if (error.status === 409) {
                Swal.fire({
                  title: 'Napaka pri naročilu',
                  text: 'Naročilo na to kmetijo že obstaja.',
                  icon: 'error',
                  confirmButtonText: 'OK'
                });
              } else {
                Swal.fire({
                  title: 'Napaka',
                  text: 'Prišlo je do napake pri naročilu na kmetijo. Poskusite znova kasneje.',
                  icon: 'error',
                  confirmButtonText: 'OK'
                });
              }
            }
          );
        }
      });
    } else {
      Swal.fire({
        title: 'Niste prijavljeni',
        text: 'Prosimo, da se prijavite, da se lahko naročite na kmetijo.',
        icon: 'info',
        confirmButtonText: 'OK'
      });
    }
  }

  getTruncatedDescription(description: string): string {
    if (description) {
      return description.length > 150 ? description.slice(0, 120) : description;
    }
    return '';
  }

  deleteFarm(farmId: number) {
    // Show confirmation dialog
    Swal.fire({
      title: 'Ste prepričani?',
      text: 'Ali res želite izbrisati to kmetijo?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Da, izbriši!',
      cancelButtonText: 'Prekliči'
    }).then((result) => {
      if (result.isConfirmed) {
        // Step 1: Remove the farm from the allFarms array
        const index = this.allFarms.findIndex(farm => farm.farmID === farmId);
        if (index !== -1) {
          // Remove the farm from the array
          this.allFarms.splice(index, 1);
        }
  
        // Step 2: Delete the farm from the server
        this.marketPageService.deleteFarm(farmId).subscribe({
          next: (response) => {
            console.log('Farm deleted:', response.body);
            // Optionally show a success message
            Swal.fire({
              title: 'Uspešno izbrisano',
              text: 'Kmetija je bila uspešno izbrisana.',
              icon: 'success',
              confirmButtonText: 'OK'
            });
          },
          error: (error) => {
            console.error('Error deleting farm:', error);
            // Optionally show an error message
            Swal.fire({
              title: 'Napaka',
              text: 'Prišlo je do napake pri brisanju kmetije. Poskusite znova kasneje.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
            // Restore the farm in case of error
            this.allFarms.splice(index, 0, { farmID: farmId } as Farm); // Add the farm back to the array
          }
        });
      }
    });
  }
  

  openSendAdminMessageModal(farmID: number) {
    console.log('Opening admin message modal for farm:', farmID);
    this.profilePageService.openAdminMessageModal(farmID);
  }
  
}
